<template>
  <div class="pc-documents">
    <pc-block class="pc-doc-request">
      <pc-request-document :backend-errors="backendErrors" @request="requestDocuments" />
    </pc-block>
    <pc-block>
      <div class="pc-documents__header">
        <div class="pc-documents__tabs">
          <div class="pc-documents__tab" :class="{'active': !showOnlyForSign, pr: !userInfo.unread_docs }" @click="changeOnlyForSign(false)">
            <span class="media-decktop">{{ $t('pc.documents.all-received') }}</span>
            <span class="media-mobile">{{ $t('pc.documents.all') }}</span><span v-if="userInfo.unread_docs" class="warning"></span>
          </div>
          <div class="pc-documents__tab" :class="{'active': showOnlyForSign}" @click="changeOnlyForSign(true)">
            {{ $t('pc.documents.waiting') }}<span v-if="userInfo.unread_unsigned_docs" class="warning"></span>
          </div>
        </div>
        <pc-select
          v-model="filter"
          icon="pc-filter"
          class="pc-documents__filter"
          :placeholder="$t('pc.documents.type-filter')"
          :options="docTypes"
          :get-option-label="opt => opt.title[$i18n.locale]"
        ></pc-select>
      </div>
      <div class="pc-documents__actions">
        <div class="pc-documents__description">
          {{ $t('pc.documents.description') }}
        </div>
        <pc-button class="pc-documents__mark" @click="markAsRead">
          {{ $t('pc.documents.mark_all_as_read') }}
        </pc-button>
      </div>
      <div class="pc-table">
        <div class="pc-table__header">
          <div
            v-for="column in columns"
            :key="column.title"
            :class="{'has-sort': column.hasSort}"
            class="pc-table__cell"
            @click="setSort(column.id)"
          >
            {{ $t(column.title) }}<icu-icon v-if="column.hasSort" class="pc-icon pc-sort" />
          </div>
        </div>
        <template v-if="data.length">
          <div
            v-for="(row,index) in data"
            :key="row.uuid"
            :class="{'is_new': row.is_new}"
            class="pc-table__row"
          >
            <div class="pc-table__cell">
              <div class="title-wrap" v-html="getTitle(row)">
              </div>
              <div class="media-tablet">
                <div>{{ getDocTypeTitle(row.doctype) }}</div>
                <div>{{ dateFormat(row.request_date) }}</div>
                <div>
                  <span>{{ $t('pc.documents.request-period') }}: </span>
                  <template v-if="row.date">
                    {{ dateFormatShort(row.date) }}
                  </template>
                  <template v-else-if="row.year">
                    {{ row.year }}
                  </template>
                  <template v-else>
                    {{ dateFormatShort(row.date_from) }} - {{ dateFormatShort(row.date_to) }}
                  </template>
                </div>
              </div>
            </div>
            <div class="pc-table__cell">
              {{ getDocTypeTitle(row.doctype) }}
            </div>
            <div class="pc-table__cell">
              {{ dateFormat(row.request_date) }}
            </div>
            <div class="pc-table__cell">
              <template v-if="row.date">
                {{ dateFormatShort(row.date) }}
              </template>
              <template v-else-if="row.year">
                {{ row.year }}
              </template>
              <template v-else>
                {{ dateFormatShort(row.date_from) }} - {{ dateFormatShort(row.date_to) }}
              </template>
            </div>
            <div class="pc-table__cell">
              <div class="actions">
                <template v-if="row.status==='PROCESSING'">
                  <div class="pc-table__loader v2"></div> <span>{{ $t('pc.documents.PROCESSING') }}</span>
                </template>
                <template v-else>
                  <div class="download-block">
                    <pc-download-progress v-if="row.is_loading" :progress="row.progress"></pc-download-progress>
                    <template v-else>
                      <div class="pc-table__action">
                        <template v-if="row.status==='SIGNING'">
                          <div class="pc-table__loader"></div>
                        </template>
                        <template v-else>
                          <div class="download" @click="showDoc(row)">
                            <template v-if="row.is_loading">
                            </template>
                            <template v-else>
                              {{ $t('pc.documents.download') }}
                            </template>
                          </div>
                        </template>
                      </div>
                    </template>
                    <icu-icon class="pc-icon pc-pdf-doc" /><b>PDF</b>
                  </div>
                  <div v-if="row.link_p7s" class="download-block">
                    <pc-download-progress v-if="row.is_p7s_loading" class="p7s-loading" :progress="row.p7s_progress"></pc-download-progress>
                    <template v-else>
                      <div class="pc-table__action">
                        <template>
                          <div class="download" @click="showDoc(row, 'sign')">
                            <template v-if="row.is_p7s_loading">
                            </template>
                            <template v-else>
                              {{ $t('pc.documents.download-sign') }}
                            </template>
                          </div>
                        </template>
                      </div>
                    </template>
                    <icu-icon class="pc-icon pc-p7s" /><b>p7s</b>
                  </div>
                  <div v-if="row.status==='APPROVED'" class="sign">
                    <div class="pc-table__action" @click="signDocument(row.uuid)">
                      {{ $t('pc.documents.sign') }}
                    </div>
                  </div>
                </template>
              </div>
            </div>
          </div>
        </template>
      </div>
    </pc-block>
  </div>
</template>

<script>
import PcBlock from '@/components/views/PrivateCabinet/PcBlock'
import IcuIcon from '@/components/common/IcuIcon'
import PcSelect from '@/components/inputs/PcSelect'
import {api} from '@/api/api'
import {mapGetters} from 'vuex'
import PcDownloadProgress from '@/components/common/PcDownloadProgress.vue'
import PcRequestDocument from '@/components/views/PrivateCabinet/PcRequestDocument.vue'
import PcButton from '@/components/common/PcButton.vue'


export default {
  name: 'PcDocuments',
  components: {PcButton, PcRequestDocument, PcDownloadProgress, PcSelect, IcuIcon, PcBlock },
  masonry: null,
  data() {
    return {
      backendErrors: {},
      updateArchive: false,
      userDocTypes:[],
      showOnlyForSign: this.$route.name==='documents-for-sign',
      columns: [
        {
          title: 'pc.documents.doc-name'
        },
        {
          id: 'doctype',
          title: 'pc.documents.doc-type',
          hasSort: true
        },
        {
          id: 'request_date',
          title: 'pc.documents.request-date',
          hasSort: true
        },
        {
          id: 'date',
          title: 'pc.documents.request-period',
          hasSort: true
        },
        {
          title: 'pc.documents.actions'
        }
      ],
      data: [],
      verifyId: null,
      limit: 20,
      offset: 0,
      hasNextPage: false,
      sort: null,
      filter: null,
    }
  },
  computed: {
    ...mapGetters(['documentTypes', 'otpConfirmed', 'userInfo', 'haveNewDocs']),
    docTypes() {
      const docTypes = this.documentTypes.filter(el => this.userDocTypes.includes(el.id))
      docTypes.unshift({title: {uk: 'Всі', en: 'All'}})
      return docTypes
    }
  },

  watch: {
    filter(){
      this.getDocuments()
    },
    otpConfirmed(newVal){
      if(newVal === this.verifyId) {
        this.getDocuments()
      }
    },
    '$route'(newVal) {
        this.showOnlyForSign = newVal.name==='documents-for-sign'
        this.getDocuments()
    },
    haveNewDocs(newVal){
      if(newVal) {
        this.updateDocuments()
        this.$store.dispatch('setHaveNewDocs', false)
      }

    }
  },

  async mounted() {
    await this.$store.dispatch('setHaveNewDocs', false)
    await this.getDocuments()
    this.$options.masonry = document.querySelector('.pc-content')
    this.$options.masonry.addEventListener('scroll', this.addScrollEvent)
  },
  beforeDestroy() {
    if(this.$options.masonry) {
      this.$options.masonry.removeEventListener('scroll', this.addScrollEvent)
    }
  },

  methods: {
    async markAsRead() {
      try {
        if(this.showOnlyForSign){
          await api.cabinet.markDocumentsForSignAsRead()
        } else {
          await api.cabinet.markAllDocumentsAsRead()
        }
        await this.getDocuments()
      }catch(err){
        console.log(err)
      }
    },
    async requestDocuments(formData, callback){
      try {
        const {data} = await api.cabinet.requestDocument(formData)
        this.getDocuments()
        callback()

      }catch(err){
        if (err.response?.data._error_code === 'BackOfficeError') {
          this.$toast.error(this.$t('notifications.something-went-wrong'))
        }
        console.log(err.response.data)
        this.backendErrors = err.response.data
      }
    },
    getTitle(row) {
      const arrTitle = row.title[this.$i18n.locale].split(' ')
      if(!row.was_read){
        let  lastWord = arrTitle[arrTitle.length - 1]
        lastWord = `<span class="lw">${lastWord}</span>`
        arrTitle[arrTitle.length - 1] = lastWord
      }
      return arrTitle.join(' ')
    },
    setSort(id) {
      if(id) {
        if (this.sort === id) {
          this.sort = '-' + id
        } else {
          this.sort = id
        }
        this.getDocuments()
      }
    },
    getDocTypeTitle(id) {
      const index = this.documentTypes.findIndex(el => el.id === id)
      return this.documentTypes[index]?.title[this.$i18n.locale]
    },
    changeOnlyForSign(val) {
      this.showOnlyForSign = val
      this.$router.push({name: val?  'documents-for-sign' : 'documents-all' })

    },
    addScrollEvent() {
      if (this.$options.masonry.scrollTop + this.$options.masonry.clientHeight >= this.$options.masonry.scrollHeight-1) {
        if (this.hasNextPage) {
          this.getDocuments(true)
        }
      }
    },
    dateFormat(value) {
      if(!value){
        return '-'
      }
      const date = new Date(value)
      return date.toLocaleString('ua-UA')
    },
    dateFormatShort(value) {
      const dateArr = this.dateFormat(value).split(',')
      return dateArr[0] !== '-' ? dateArr[0] : ''
    },

    async updateDocuments() {
      const documentApi = this.showOnlyForSign ? api.cabinet.getDocumentsForSign : api.cabinet.getDocuments
      let limit = this.data.length
      if(this.data.length % this.limit) {
        limit++
        this.hasNextPage = true
      }
      const params = {
        limit,
        offset: 0,
        doctype: this.filter,
        ordering: this.sort,
      }

      try {
        const {data} = await documentApi(params)
        const newDocList = data.results
        const newDocIndex = newDocList.findIndex(doc => {
          return -1 === this.data.findIndex(el => el.uuid === doc.uuid)
        })

        if(newDocIndex > -1){
         newDocList[newDocIndex].is_new = true
        }
        this.data = newDocList

        if(newDocIndex > -1){
          setTimeout(()=> {
            this.data[newDocIndex].is_new = false
          },1000)
        }
      }catch(err) {
        console.log(err)
      }



    },
    async getDocuments(getNext = false){
      const documentApi = this.showOnlyForSign ? api.cabinet.getDocumentsForSign : api.cabinet.getDocuments
      if(getNext) {
        ++this.offset
      } else {
        this.offset = 0
      }
      const params = {
        limit: this.limit,
        offset: this.offset * this.limit,
        doctype: this.filter,
        ordering: this.sort,
      }
      try {
        const {data} = await documentApi(params)
        if(getNext) {
          this.data.push(...data.results)
        } else {
          this.data = data.results
        }

        if(this.showOnlyForSign) {
          this.userDocTypes = data.unsigned_doctypes
        } else {
          this.userDocTypes = data.doctypes
        }
        this.$store.dispatch('setUserInfo')
        this.hasNextPage = data.next
      } catch (err) {
        console.log(err)
      }
    },
    signDocument(id) {
      this.verifyId = id
      this.$store.dispatch('showOtp',{
        title: 'verification-modal.otp-was-sent',
        id,
        getCodeApi: api.cabinet.getDocumentsSignOtp,
        getCodeApiData:{
          docs: [
            { uuid: id }
          ],
          sign: true
        },
        confirmCodeApi: api.cabinet.confirmDocumentsSignOtp,
        showContacts: false,
        hideCountdown: false
      })
    },
    async showDoc(row, docType = 'doc'){
      const progressType = docType === 'doc'? 'progress' : 'p7s_progress'
      const linkType = docType === 'doc'? 'link' : 'link_p7s'
      this.$set(row, progressType, 0)
      const index = this.data.findIndex(el => el.uuid === row.uuid)
      this.$set(this.data[index], 'was_read', true)
      const loadingType = docType === 'doc'? 'is_loading' : 'is_p7s_loading'
      this.$set(this.data[index], loadingType, true)
      try{
        const res = await api.cabinet.getDoc(row[linkType], progressEvent => {
          let percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total)
          row[progressType] = percentCompleted
        })
        await this.$store.dispatch('setUserInfo')
        this.hasUnread = this.userInfo.unread_docs
        this.data[index][loadingType]= false
        const val = res.data
        const blob = new Blob([val], { type: docType === 'doc'? 'application/pdf' : 'application/x-pkcs7-signature' })

        const url = window.URL || window.webkitURL
        const link = url.createObjectURL(blob)
        const a = document.createElement('a')
        a.setAttribute('href', link)
        if(!/iPhone|iPad|iPod/i.test(navigator.userAgent)) {
          if(docType === 'doc') {
            a.setAttribute('target', '_blank')
          }
        }
        if(docType !== 'doc') {
          a.setAttribute('download', row.title[this.$i18n.locale] + '.p7s')
        }
        document.body.appendChild(a)

        a.click()
        document.body.removeChild(a)



      }catch(err){
        console.log(err)
        this.$toast.error(this.$t('notifications.something-went-wrong'))
      }
    }
  }
}
</script>

<style scoped lang="scss">
@import "~@/assets/scss/theme.scss";

.pc-documents {

  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding-bottom: 48px;
    @media (max-width: $desktop){
      flex-direction: column;
      align-items: stretch;
    }
  }
  &__actions {
    display: flex;
    align-items: center;
    padding-bottom: 46px;
    justify-content: space-between;
    column-gap: 20px;

    @media (max-width: $desktop) {
      align-items: flex-start;
      flex-direction: column;
      column-gap: 0;
      row-gap: 20px;
    }
  }
  &__mark {
    min-height: 0;
    height: 40px;
    width: 336px;
  }

  &__description {

    color: $passive-text;
    font-size: 16px;
    font-weight: 400;
  }
  &__filter {
    width: 336px;
    @media (max-width: $desktop){
      width: 100%;
      margin-top: 35px;
    }
  }

  &__tabs {
    display: flex;
    justify-content: flex-start;
    align-items: center;
  }
  &__tab  {
    font-size: 20px;
    line-height: 40px;
    font-weight: 500;
    position: relative;
    cursor: pointer;
    color: $passive-text;
    white-space: nowrap ;
    flex-wrap: nowrap;
    flex-shrink: 0;
    display: flex;
    align-items: center;

    * {
      color: $passive-text;
    }

    &:first-child {
      margin-right: 40px;
    }

    &.active {
      color: $default-text;
      * {
        color: $default-text;
      }
      &:after {
        content: '';
        display: block;
        position: absolute;
        bottom: 0;
        left: 0;
        right: 0;
        height: 4px;
        border-radius: 4px;
        background: linear-gradient(90deg, #051E36 0%, #73BEE7 100%);
      }
    }
  }
  .media-tablet {
    * {
    color: $passive-text;
    margin-top: 3px;
    }
    span {
      color: $default-text;
    }
  }
  .media-mobile *{
    color: $passive-text;
    margin-top: 3px;
  }


  .pc-table__cell {
    flex-grow: 1;
    &:first-child {
      flex-basis: 30%;
      width: 30%;
    }
    &:nth-child(2) {
      flex-basis: 17%;
      width: 17%;
    }
    &:nth-child(3) {
      flex-basis: 14.5%;
      width: 14.5%;
    }
    &:nth-child(4) {
      flex-basis: 20%;
      width: 20%;
    }
    &:last-child {
      flex-basis: 20%;
      width: 20%;
    }
    @media (max-width: $desktop){
      &:first-child {
        flex-direction: column;
        align-items: flex-start;
      }
      &:nth-child(2) {
        display: none;
      }
      &:nth-child(3) {
        display: none;
      }
      &:nth-child(4) {
        display: none;
      }
    }
    @media (max-width: $tablet){
      &:first-child {
        flex-direction: column;
        align-items: flex-start;
      }
    }
  }

  @media (max-width: $tablet){
    .media-decktop {
      display: none;
    }
    .pc-table {
      &__header {
        .pc-table__cell {
          &:nth-child(5) {
            display: none;
          }
        }
      }
      &__cell {
        &:first-child {
          width: 100%;
          flex-basis: 100%;
        }
        &:nth-child(5) {
          width: 100%;
          flex-basis: 100%;

          .actions {
            width: 100%;
            display: flex;
            .pc-icon {
              flex-shrink: 0;
              margin-right: 16px;
            }
          }
        }
      }
      &__row {
        flex-direction: column;
        align-items: stretch;
      }
    }
  }

  .actions {
    .pc-icon {
      margin-left: 12px;
      margin-top: -2px
    }
  }
}

.pc-table__loader {
  margin-left: 31px;
  margin-right: 31px;

  &.v2 {
    margin-left: 0;
    margin-right: 10px;
    margin-top: -3px;
  }
}
.download {
  width: 86px;
}

.pc-doc-request {
  max-width: 715px;
  @media (max-width:$desktop) {
    max-width: 100%;
  }
}

.download-block {
  width: 100%;
  flex-shrink: 0;
  display: flex;
  margin-bottom: 5px;
  align-items: center;
  @media (max-width: $desktop) {
    margin-bottom: 0;
    margin-top: 10px;
    .download {
      width: auto;
  }

  }
}
.p7s-loading {
  margin-top: 13px;
  margin-bottom: 13px;
  @media (max-width: $desktop) {
    margin-top: 0;
    margin-bottom: 0;
  }
}

.pc-table__row {
  interpolate-size: allow-keywords;
  transition: opacity 1.5s, height 1s, padding 1s;
}

.is_new {
  height: 0;
  padding-top: 0;
  padding-bottom: 0;
  opacity: 0
}
</style>
